import React from "react";
import { TextField } from "@mui/material";
import { WebhookAuthFields } from "./WebhookAuthFields";
import {
  WebhookAuthField,
  WebhookAuthValues,
  WebhookFieldErrors,
} from "../utils/webhookAuth";
import { jsonTextError } from "../pages/Sites/SiteAboutPage/SiteAboutPageUtils/siteWebhooks";

export const WEBHOOK_METHOD_OPTIONS = ["POST", "GET"];

export interface SiteWebhookFormValues {
  display_name: string;
  target_url: string;
  method: string;
  message_body: string;
  auth: WebhookAuthValues;
}

export type SiteWebhookTextField =
  | "display_name"
  | "target_url"
  | "method"
  | "message_body";

export const EMPTY_SITE_WEBHOOK_FORM_VALUES: SiteWebhookFormValues = {
  display_name: "",
  target_url: "",
  method: "POST",
  message_body: "{}",
  auth: {
    auth_type: null,
    auth_endpoint: null,
    auth_body: null,
    auth_token_path: null,
  },
};

/**
 * The site-webhook create form (ENG-2233), shared by the onboarding Webhooks
 * step and the Site About "+ Add Webhook" dialog so both post the same shape
 * to `webhook_customer/`. Controlled: the parent owns the values.
 */
export const SiteWebhookFormFields = ({
  values,
  onFieldChange,
  onAuthFieldChange,
  fieldErrors = {},
  width = 350,
  messageBodyHelperText = "*optional",
  hideMessageBody = false,
}: {
  values: SiteWebhookFormValues;
  onFieldChange: (field: SiteWebhookTextField, value: string) => void;
  onAuthFieldChange: (field: WebhookAuthField, value: string | null) => void;
  fieldErrors?: WebhookFieldErrors;
  width?: number;
  messageBodyHelperText?: string;
  /** The Site About setup flow edits the body in its payload builder (ENG-2427). */
  hideMessageBody?: boolean;
}) => {
  const messageBodyError =
    fieldErrors.message_body ||
    jsonTextError(values.message_body, "Message Body");

  return (
    <div className="flex flex-col gap-5" data-testid="site-webhook-form">
      <TextField
        label="Display Name"
        variant="standard"
        value={values.display_name}
        onChange={(event) => {
          const nextValue = event.target.value;
          onFieldChange("display_name", nextValue);
        }}
        error={Boolean(fieldErrors.display_name)}
        helperText={fieldErrors.display_name || ""}
        inputProps={{ "aria-label": "Display Name" }}
        sx={{ fontFamily: "Mulish", width }}
      />
      <TextField
        label="Target URL"
        variant="standard"
        value={values.target_url}
        onChange={(event) => {
          const nextValue = event.target.value;
          onFieldChange("target_url", nextValue);
        }}
        error={Boolean(fieldErrors.target_url)}
        helperText={fieldErrors.target_url || ""}
        inputProps={{ "aria-label": "Target URL" }}
        sx={{ fontFamily: "Mulish", width }}
      />
      <TextField
        select
        SelectProps={{ native: true }}
        label="Method"
        variant="standard"
        value={values.method}
        onChange={(event) => {
          const nextValue = event.target.value;
          onFieldChange("method", nextValue);
        }}
        error={Boolean(fieldErrors.method)}
        helperText={fieldErrors.method || ""}
        InputLabelProps={{ shrink: true }}
        inputProps={{ "aria-label": "Method" }}
        sx={{ fontFamily: "Mulish", width: 120 }}
      >
        {WEBHOOK_METHOD_OPTIONS.map((method) => (
          <option key={method} value={method}>
            {method}
          </option>
        ))}
      </TextField>
      {!hideMessageBody && (
        <TextField
          multiline
          rows={4}
          label="Message Body"
          variant="outlined"
          value={values.message_body}
          onChange={(event) => {
            const nextValue = event.target.value;
            onFieldChange("message_body", nextValue);
          }}
          error={Boolean(messageBodyError)}
          helperText={messageBodyError || messageBodyHelperText}
          inputProps={{ "aria-label": "Message Body", spellCheck: false }}
          sx={{ fontFamily: "Mulish", width }}
        />
      )}
      <WebhookAuthFields
        values={values.auth}
        errors={fieldErrors}
        onFieldChange={onAuthFieldChange}
        width={width}
      />
    </div>
  );
};
